/**
 *  2025.05.08 한영광
 *  서비스 보고서 모달
 */
import { IModal } from 'customTypes';
import { ModalBackground, Modal, LoadingModalBackground } from '../../assets/styles/Modal';
import { useTranslation } from 'react-i18next';
import { BtnBlue, BtnGhost, BtnGreen, BtnRed, BtnViolet } from '../Button';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import ServiceReport, { gubunOptions } from '../ServiceReport';
import { apiGet, apiPatch, apiPost } from '../../services/_common';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { logPost } from '../../services/log';
import { isMobile } from '../../utils/checkMobile';
import Portal from '../Portal';
import ConfirmModal from './ConfirmModal';
import { PulseLoader } from 'react-spinners';
import ReactSignatureCanvas from 'react-signature-canvas';
import { downloadFile, setFlutterWebViewViewport, trimCanvas } from '../../utils/flutterBridge';
import useBackHistory from '../../hooks/useBackHistory';
import html2canvas from 'html2canvas';
import LoadButton from '../button/LoadButton';

const ReportRoot = styled.div`
  width: 60rem;
  max-width: 98vw;
  min-width: 340px;
  height: 90dvh;
  min-height: 600px;
  max-height: 90dvh;
  background-color: var(--board);
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: var(--text-primary);

  #preview {
    // 이미지 다운로드 스타일
    position: absolute;
    top: -9999px;
    left: -9999px;
    width: 800px;
    height: auto;
    background: #fff;
    z-index: 999;

    .service-report-table {
      background-color: #fff !important;
    }

    table {
      margin: 24px 0;
      table-layout: fixed !important;
      background-color: #fff !important;

      td {
        border-right-color: #18181b !important;
        border-bottom-color: #18181b !important;
        color: #18181b !important;
      }

      p,
      span,
      pre {
        color: #18181b !important;
      }

      span.material-symbols-rounded {
        color: #fff !important;
      }

      td.border-left {
        border-left-color: #18181b !important;
      }

      td.border-top {
        border-top-color: #18181b !important;
      }

      .form-title {
        background-color: #f3f4f6 !important;
      }

      .check-box-cont {
        border-color: #18181b !important;

        &.checked {
          background-color: #18181b !important;
        }

        .material-symbols-rounded {
          color: #fff !important;
        }
      }
    }

    .flutter-table {
      td p,
      td span,
      td pre {
        padding-bottom: 8px !important;
      }

      td img {
        margin-top: 4px !important;
      }
    }

    .file-download-wrap {
      display: none;
    }
  }

  &.pc-viewport {
    width: 100dvw;
    max-width: 100dvw;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;

    .inputForm-head {
      padding: 4vw;
      font-size: 5vw;

      .closeBtn {
        span {
          font-size: 5vw;
        }
      }
    }

    .inputForm-body {
      overflow-x: hidden;
    }

    .footer {
      padding: 3vw 4vw;
      font-size: 5vw;
      button {
        padding: 4vw 4vw;
        font-size: 5vw;
      }
    }
  }

  .inputForm-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid var(--outline);
    font-weight: 600;
    font-size: 1.25rem;
    background-color: var(--board);
    flex-shrink: 0;
  }
  .inputForm-body {
    flex: 1 1 0;
    min-height: 0;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    background-color: var(--board);
    overflow-y: auto;
    max-height: calc(100dvh - 130px); /* 헤더+푸터 제외 */

    @media screen and (max-width: 768px) {
      /* 모바일에서 키보드가 나타날 때를 대비한 스타일 */
      padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    }
  }
  .input-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }
  .input-col {
    flex: 1;
  }
  .table-wrap {
    overflow-x: auto;
    max-height: 320px;
    min-height: 60px;
    background-color: var(--background);
    border-radius: 0.25rem;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.95rem;
    th,
    td {
      border: 1px solid var(--outline);
      text-align: center;
    }
    th {
      background-color: var(--tonal-light);
      font-weight: 500;
    }
  }
  .footer {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem;
    border-top: 1px solid var(--outline);
    background-color: var(--tonal-light);
    flex-shrink: 0;

    button {
      padding: 0.5rem 1rem;
      height: 3rem;
    }

    .clear {
      border: 1px solid var(--outline);
    }

    @media screen and (max-width: 768px) {
      /* 모바일에서 footer를 하단에 고정하고 불필요한 여백 제거 */
      position: sticky;
      bottom: 0;
      margin-bottom: 0;
      /* env(safe-area-inset-bottom) 제거: Flutter WebView에서 이 값이 0이 아니라
         footer 버튼 아래에 시스템바 높이만큼 빈 영역이 생기던 문제. 위 padding과 동일하게 1rem. */
      padding-bottom: 1rem;
      z-index: 10;
    }
  }
  .signImg-container {
    border: 1px solid var(--outline);
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: var(--background);
    display: flex;
    align-items: center;
    gap: 1rem;
    width: fit-content;
    max-width: 350px;
    justify-content: space-between;
    margin-left: auto;
  }
  .signImg-title {
    font-weight: 600;
    font-size: 1rem;
    color: var(--primary);
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
    white-space: nowrap;
  }
  .signImg-container img {
    max-width: 90px;
    max-height: 60px;
    display: block;
    margin: 0;
    border: 1px solid var(--outline);
    border-radius: 0.25rem;
    background: #fafafa;
  }

  @media screen and (max-width: 768px) {
    max-width: 100dvw;
    max-height: 100dvh;
    width: 100dvw;
    height: 100dvh;
    border-radius: 0;
    /* 모바일에서 키보드가 나타날 때 불필요한 여백 제거 */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    .inputForm-body {
      /* 키보드가 나타날 때 동적으로 높이 조정 */
      max-height: none;
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
`;

const SignatureContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px; /* 기본 마진 추가 */

  @media (max-width: 480px) {
    margin-bottom: 40px; /* 모바일에서 더 큰 마진 추가 */
  }
`;

const SignatureCanvasWrapper = styled.div`
  border: 2px dashed var(--primary);
  border-radius: 4px;
  width: 100%;
  max-width: 774px;
  height: 340px;
  background-color: #fff;
  margin-bottom: 10px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`;

const StyledSignatureCanvas = styled(ReactSignatureCanvas)`
  width: 100% !important;
  height: 100% !important;
  position: absolute;
  top: 0;
  left: 0;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

const SignatureHint = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #999;
  font-size: 16px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.7;

  @media screen and (max-width: 1024px) {
    font-size: 6vw;
  }
`;

const LoadingModalRoot = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 5010;

  > span {
    width: auto;
    height: auto;
  }
`;

const initialState: IServiceReport = {
  smIdx: 0,
  cCd: '',
  hCd: '',
  hName: '',
  sCd: '',
  sName: '',
  address: '',
  signManagerName: '',
  signManagerPhone: '',
  signManagerPartname: '',
  companyManagerName: '',
  companyManagerPhone: '',
  mainManagerId: '',
  subManagerId: '',
  mainManagerName: '',
  subManagerName: '',
  mainManagerPhone: '',
  subManagerPhone: '',
  mainManagerSignUrl: '',
  subManagerSignUrl: '',
  bigo: '',
  useMaterial: '',
  maintenanceTitle: '',
  maintenanceCauses: '',
  maintenanceAction: '',
  installTitle: '',
  installDate: ['', '', '', ''],
  installList: [{ name: '', unit: '', qty: '', location: '', bigo: '', gubun: { name: '', gubun: '' }, date1: '', date2: '', date3: '', date4: '' }],
  isInstall: 'Y',
  isMaintenance: 'Y',
  isPaid: 'Y',
  signImg: '',
  scheduleRFList: [],
  completeDate: '',
  writer: '',
  editor: '',
  wDate: '',
  eDate: '',
  isSent: 0,
};

// installList의 gubun을 객체 형태로 변환하는 헬퍼 함수
const convertInstallListGubun = (installList: any[]) => {
  if (!installList || !Array.isArray(installList)) {
    return [{ name: '', unit: '', qty: '', location: '', bigo: '', gubun: { name: '', gubun: '' }, date1: '', date2: '', date3: '', date4: '' }];
  }
  return installList.map((el: any) => {
    // gubun이 이미 객체 형태인 경우
    if (typeof el.gubun === 'object' && el.gubun !== null) {
      return el;
    }
    // gubun이 문자열인 경우 객체로 변환
    const gubunValue = typeof el.gubun === 'string' ? el.gubun : '';
    const gubunOption = gubunOptions.find((option) => option.value === gubunValue);
    return {
      ...el,
      gubun: {
        name: gubunOption?.label || '',
        gubun: gubunValue || '',
      },
    };
  });
};

const ServiceReportModal = ({ openModal, setOpenModal, state }: any) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true); // 로드 상태
  const [form, setForm] = useState<IServiceReport>(initialState);
  const [openConfirmModal, setOpenConfirmModal] = useState<any>(false);
  const [isPcViewport, setIsPcViewport] = useState<boolean>(false); // pc viewport 여부 [ 모바일에서 pc 뷰포트 적용 여부 ]
  const [mainManagerCd, setMainManagerCd] = useState({ type: 'mainManagerCd', mainManagerCd: '', cdName: t('미선택') }); // 담당자(정)
  const [subManagerCd, setSubManagerCd] = useState({ type: 'subManagerCd', subManagerCd: '', cdName: t('미선택') }); // 담당자(부)
  const [mainManagerOptions, setMainManagerOptions] = useState<any>([{ type: 'mainManagerCd', mainManagerCd: '', cdName: t('미선택') }]);
  const [subManagerOptions, setSubManagerOptions] = useState<any>([{ type: 'subManagerCd', subManagerCd: '', cdName: t('미선택') }]);
  const [deleteFileIndex, setDeleteFileIndex] = useState<number[]>([]);
  const [isSignView, setIsSignView] = useState<boolean>(false); // 서명 화면 여부
  const [dataUrl, setDataUrl] = useState<string>(''); // 이미지 BASE64 URL
  const [isSigned, setSigned] = useState<boolean>(false); // 서명 상태
  const signatureCanvasWrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<ReactSignatureCanvas>(null);
  const mobile = isMobile();
  const originalViewport = 'initial-scale=1.0, user-scalable=no, maximum-scale=1, width=device-width, minimal-ui, viewport-fit=auto';
  const signManagerNameRef = useRef<HTMLInputElement>(null);
  const signManagerPhoneRef = useRef<HTMLInputElement>(null);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false); // 필수값 여부
  const previewElement = document.getElementById('preview') as HTMLDivElement; // 미리보기
  const screenElement = document.getElementById('screen') as HTMLElement;
  const [isSaveLoading, setIsSaveLoading] = useState(false); // 진행 상태

  // 닫기버튼 클릭
  const onClickClose = () => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
  };

  const { onBackPrev } = useBackHistory({ isOpen: openModal.status, onPrev: onClickClose, pageName: 'service_report_modal' });

  useEffect(() => {
    if (state.smIdx) {
      getServiceReport();
    } else {
      setForm({
        ...initialState,
        cCd: state.cCd,
        hCd: state.hCd,
        hName: state.hCd ? state.hName : '',
        sCd: state.sCd,
        sName: state.sCd ? state.sName : '',
        address: state.sCd ? state.address : '',
        smIdx: state.smIdx,
        isPaid: state.isPaid,
        signManagerName: state.siteManagerName,
        signManagerPhone: state.siteManagerPhone,
        signManagerPartname: state.siteManagerPartname,
      });
    }
  }, [state]);

  // 페이지별 viewport 설정
  useEffect(() => {
    // PC 양식지를 모바일에서 볼 때 풀 화면으로 UI를 유지하기 위해 문서 viewport 설정
    const $body = document.body as HTMLBodyElement;
    const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
    setIsLoading(true);

    if (!viewportMeta) {
      return () => {};
    }

    if (mobile && window.innerWidth < 768 && (form.signImg || isSignView)) {
      $body.style.touchAction = 'auto';
      // viewport 메타 태그와 Flutter WebView 설정을 일치시킴
      const pcViewport = 'width=850';
      viewportMeta.content = pcViewport;
      setIsPcViewport(true);
      getServiceReport().then(() => setIsLoading(false));
      // 안드로이드 모바일 뷰포트 설정
      setFlutterWebViewViewport(pcViewport);
    } else {
      $body.style.touchAction = 'none';
      viewportMeta.content = originalViewport;
      setIsPcViewport(false);
      getServiceReport().then(() => setIsLoading(false));
      // 안드로이드 모바일 뷰포트 설정 해제
      setFlutterWebViewViewport(originalViewport);
    }

    return () => {
      viewportMeta.content = originalViewport;
      $body.style.touchAction = 'none';
    };
  }, [mobile, form.signImg, isSignView]);

  // 담당자 옵션 설정
  useEffect(() => {
    if (state.companyUser && state.companyUser.length > 0) {
      setMainManagerOptions([
        { type: 'mainManagerCd', mainManagerCd: '', cdName: t('미선택') },
        ...(state.companyUser?.map((el: any) => ({ type: 'mainManagerCd', mainManagerCd: el.userId, cdName: el.userName })) || []),
      ]);
      setSubManagerOptions([
        { type: 'subManagerCd', subManagerCd: '', cdName: t('미선택') },
        ...(state.companyUser?.map((el: any) => ({ type: 'subManagerCd', subManagerCd: el.userId, cdName: el.userName })) || []),
      ]);
    }
  }, [state.companyUser]);

  // 담당자(정) 선택시
  useEffect(() => {
    if (mainManagerCd.mainManagerCd !== '') {
      setForm((prev: any) => ({ ...prev, mainManagerId: mainManagerCd.mainManagerCd }));
    } else {
      setMainManagerCd({ type: 'mainManagerCd', mainManagerCd: '', cdName: t('미선택') });
      setForm((prev: any) => ({ ...prev, mainManagerId: '' }));
    }
  }, [mainManagerCd.mainManagerCd]);

  // 담당자(부) 선택시
  useEffect(() => {
    if (subManagerCd.subManagerCd !== '') {
      setForm((prev: any) => ({ ...prev, subManagerId: subManagerCd.subManagerCd }));
    } else {
      setSubManagerCd({ type: 'subManagerCd', subManagerCd: '', cdName: t('미선택') });
      setForm((prev: any) => ({ ...prev, subManagerId: '' }));
    }
  }, [subManagerCd.subManagerCd]);

  const onClickSignSave = async () => {
    if (dataUrl === '') {
      toast.warn(t('서명을 해주세요'));
    } else {
      const base64 = dataUrl.split(',')[1];
      const { smIdx, cCd, hCd, sCd } = form;
      const req = {
        smIdx,
        cCd,
        hCd,
        sCd,
        signImg: base64,
      };
      const res = await apiPost({ path: `/schedule/report/sign`, req, contentType: 'application/json' });
      if (res.status === 200) {
        toast.success(t('서명이 저장되었습니다'));
        setIsSignView(false);
        getServiceReport();
      }
    }
  };

  // 서명 취소
  const onClickSignCancel = () => {
    setIsSignView(false);
    setSigned(false);
    setDataUrl('');
  };

  const clearSignature = () => {
    canvasRef.current?.clear();
    setDataUrl('');
    setSigned(false);
  };

  useEffect(() => {
    // 캔버스 사이즈를 컨테이너에 맞게 조정
    const resizeCanvas = () => {
      if (signatureCanvasWrapperRef.current && canvasRef.current && isSignView) {
        const canvas = canvasRef.current;
        // const ratio = Math.max(window.devicePixelRatio || 1, 1);

        const ctx = canvas.getCanvas().getContext('2d');
        if (ctx) {
          canvas.getCanvas().width = signatureCanvasWrapperRef.current.offsetWidth;
          canvas.getCanvas().height = signatureCanvasWrapperRef.current.offsetHeight;

          // ctx.scale(ratio, ratio);
          canvas.getCanvas().style.width = `${signatureCanvasWrapperRef.current.offsetWidth}px`;
          canvas.getCanvas().style.height = `${signatureCanvasWrapperRef.current.offsetHeight}px`;
        }
      }
    };

    resizeCanvas();

    window.addEventListener('load', resizeCanvas);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [canvasRef, signatureCanvasWrapperRef, isSignView, form]);

  // 휴대폰 번호 하이픈 처리
  const handleSiteManagerPhone = (phone: string) => {
    // 빈 문자열이면 빈 문자열로 반환
    if (!phone || phone === '') return '';

    // 하이픈이 있으면 제거 및 숫자만 남기기
    const phoneNumber = phone.replaceAll('-', '').replace(/\D/g, '');

    // 길이가 길이에 따라 하이픈 삽입
    let match = phoneNumber.match(/^(\d{3})(\d{4})(\d{4})$/); // 11자리 예시
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }

    match = phoneNumber.match(/^(\d{2})(\d{4})(\d{4})$/); // 10자리 예시
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }

    return phone;
  };

  const getServiceReport = async () => {
    const res = await apiGet({ path: '/schedule/report', req: { smIdx: state.smIdx }, contentType: 'application/json' });
    const { status, data: resData } = res;
    if (status === 200 && resData.data) {
      const mainMangerValue = state?.users.length > 0 ? { value: state.users[0]?.value, label: state.users[0]?.label } : { value: '', label: t('미선택') };
      const subMangerValue = state?.users.length > 1 ? { value: state.users[1]?.value, label: state.users[1]?.label } : { value: '', label: t('미선택') };
      setMainManagerCd({
        type: 'mainManagerCd',
        mainManagerCd: res.data.data.mainManagerId ?? mainMangerValue.value,
        cdName: res.data.data.mainManagerName ?? mainMangerValue.label,
      });
      setSubManagerCd({
        type: 'subManagerCd',
        subManagerCd: res.data.data.subManagerId ?? subMangerValue.value,
        cdName: res.data.data.subManagerName ?? subMangerValue.label,
      });
      setForm({
        ...initialState,
        ...resData.data,
        cCd: res.data.data.cCd || state.cCd,
        hCd: res.data.data.hCd || state.hCd,
        hName: res.data.data.hName || state.hName,
        sCd: res.data.data.sCd || state.sCd,
        sName: res.data.data.sName || state.sName,
        address: res.data.data.address || state.address,
        smIdx: res.data.data.smIdx || state.smIdx,
        isPaid: res.data.data.isPaid || state.isPaid,
        signManagerName: res.data.data.signManagerName || state.siteManagerName,
        signManagerPhone: res.data.data.signManagerPhone || state.siteManagerPhone,
        signManagerPartname: res.data.data.signManagerPartname || state.siteManagerPartname,
        mainManagerId: res.data.data.mainManagerId ?? mainMangerValue.value,
        subManagerId: res.data.data.subManagerId ?? subMangerValue.value,
        installDate: resData.data.installDate || ['', '', '', ''],
        installList: convertInstallListGubun(resData.data.installList),
        scheduleRFList: resData.data.scheduleRFList.map((el: { rfIndex: number; rfUrl: string; smIdx: number }) => ({
          fileId: el.rfIndex,
          file: '',
          fileName: el.rfUrl.slice(el.rfUrl.lastIndexOf('/') + 1),
          fileFormat: el.rfUrl.slice(el.rfUrl.lastIndexOf('.') + 1),
          fUrl: el.rfUrl,
        })),
        // installList: JSON.parse(res.data.data.installList),
      });
    } else {
      setMainManagerCd({
        type: 'mainManagerCd',
        mainManagerCd: state.users.length > 0 ? state.users[0].value : '',
        cdName: state.users.length > 0 ? state.users[0].label : t('미선택'),
      });
      setSubManagerCd({
        type: 'subManagerCd',
        subManagerCd: state.users.length > 1 ? state.users[1].value : '',
        cdName: state.users.length > 1 ? state.users[1].label : t('미선택'),
      });
      setForm({
        ...initialState,
        cCd: state.cCd,
        hCd: state.hCd,
        hName: state.hCd ? state.hName : '',
        sCd: state.sCd,
        sName: state.sCd ? state.sName : '',
        address: state.sCd ? state.address : '',
        smIdx: state.smIdx,
        isPaid: state.isPaid,
        signManagerName: state.signManagerName,
        signManagerPhone: state.signManagerPhone,
        signManagerPartname: state.signManagerPartname,
      });
    }
  };

  // 전송 API
  const sendServiceReport = async () => {
    const {
      hName,
      sName,
      wDate,
      eDate,
      writer,
      editor,
      signImg,
      isSent,
      companyManagerName,
      companyManagerPhone,
      mainManagerName,
      mainManagerPhone,
      subManagerName,
      subManagerPhone,
      scheduleRFList,
      mainManagerSignUrl,
      subManagerSignUrl,
      installDate,
      // siteManagerName,
      // siteManagerPartname,
      // siteManagerPhone,
      ...rest
    } = form;
    const req = {
      ...rest,
      mainManagerId: mainManagerCd.mainManagerCd,
      subManagerId: subManagerCd.subManagerCd,
      installList: JSON.stringify(form.installList),
      installDate: JSON.stringify(form.installDate.map((el: string) => (el !== '' ? el : ''))),
    };
    const res = await apiPost({ path: '/schedule/report/send', req, contentType: 'application/json' });
    if (res.status === 200) {
      setForm({
        ...res.data.data,
        scheduleRFList: res.data.data.scheduleRFList.map((el: { rfIndex: number; rfUrl: string; smIdx: number }) => ({
          fileId: el.rfIndex,
          file: '',
          fileName: el.rfUrl.slice(el.rfUrl.lastIndexOf('/') + 1),
          fileFormat: el.rfUrl.slice(el.rfUrl.lastIndexOf('.') + 1),
          fUrl: el.rfUrl,
        })),
      });
      toast.success(t('전송되었습니다.'));
      await logPost({
        hCd: state.cCd,
        sCd: '00000',
        userId: state.userId,
        menu: '서비스 보고서 모달',
        action: '서비스 보고서 메시지 전송',
        etc: `현장 담당자 : ${form.signManagerName} ${form.signManagerPhone}`,
      });
    } else if (res.status === 400) {
      toast.warn(t('이미 전송된 보고서입니다.'));
    }
  };

  // 전송 버튼 클릭
  const onClickSend = () => {
    const completeDateDom = document.querySelector('.inputMask') as HTMLInputElement;

    if (!form.completeDate) {
      toast.error(t('완료일을 입력해주세요.'));
      completeDateDom && completeDateDom.focus();
    } else if (form.isInstall === 'N' && form.isMaintenance === 'N') {
      toast.error(t('설치 또는 유지보수 중 하나 이상을 선택해주세요.'));
    } else if (!form.signManagerName || !form.signManagerPhone) {
      toast.error(t('현장 서명자 정보를 입력해주세요.'));
    } else if ((form.scheduleRFList.filter((el: IFile) => el.fUrl === '').map((el: IFile) => el.file) || []).length > 0) {
      toast.info(t('첨부된 파일이 있습니다. 저장을 먼저 진행해주세요.'));
    } else if (deleteFileIndex.length > 0) {
      toast.info(t('삭제된 파일이 있습니다. 저장을 먼저 진행해주세요.'));
    } else if (form.isSent >= 1) {
      setOpenConfirmModal({ status: true, type: 'sent_confirm', api: sendServiceReport });
    } else {
      sendServiceReport();
    }
  };

  // IServiceReport & { files?: File[]; deleteFileIndex?: number[] }
  const getReq = (reqData: any) => {
    const formData = new FormData();
    Object.keys(reqData).forEach((key) => {
      if (key === 'files') {
        reqData[key]?.forEach((file: File) => {
          formData.append(key, file); // 'files' 키로 각 파일 append
        });
      } else if (key === 'installList' || key === 'installDate') {
        // installList나 deleteFileIndex는 JSON 문자열로 변환하여 하나의 키로 append
        formData.append(key, JSON.stringify(reqData[key]));
        // formData.append(key, new Blob([JSON.stringify(reqData[key])], { type: 'application/json' }));
      } else if (key === 'deleteFileIndex') {
        reqData[key].forEach((id: any, i: number) => {
          formData.append(`deleteFileIndex[${i}]`, id);
        });
      } else {
        formData.append(key, reqData[key] ?? '');
      }
    });
    return formData;
  };

  const onClickSave = async (type: 'save' | 'modify') => {
    setIsSaveLoading(true);

    const {
      hName,
      sName,
      wDate,
      eDate,
      writer,
      editor,
      signImg,
      isSent,
      mainManagerName,
      mainManagerPhone,
      subManagerName,
      subManagerPhone,
      scheduleRFList,
      mainManagerSignUrl,
      subManagerSignUrl,
      companyManagerName,
      companyManagerPhone,
      // siteManagerName,
      // siteManagerPhone,
      // siteManagerPartname,
      ...rest
    } = form;
    // const files = { files: form.scheduleRFList.filter((el: IFile) => el.fUrl === '').map((el: IFile) => el.file) };

    const setData = {
      ...rest,
      // installList: JSON.stringify(form.installList),
      installList: form.installList.map((el: any) => ({ ...el, gubun: el.gubun.gubun ?? '' })),
      installDate: form.installDate.map((el: string) => (el !== '' ? el : '')),
      files: form.scheduleRFList.filter((el: IFile) => el.fUrl === '').map((el: IFile) => el.file) || [],
      deleteFileIndex,
    };
    let res;
    if (wDate) {
      const req = getReq({ ...setData });
      res = await apiPatch({ path: '/schedule/report', req, contentType: 'multipart/form-data' });
    } else {
      delete (setData as any).deleteFileIndex;
      const req = getReq({ ...setData });
      res = await apiPost({ path: '/schedule/report', req, contentType: 'multipart/form-data' });
    }
    if (res.status === 200) {
      setIsSaveLoading(false);
      setDeleteFileIndex([]);
      setMainManagerCd({ type: 'mainManagerCd', mainManagerCd: res.data.data.mainManagerId, cdName: res.data.data.mainManagerName });
      setSubManagerCd({ type: 'subManagerCd', subManagerCd: res.data.data.subManagerId, cdName: res.data.data.subManagerName });
      setForm({
        ...res.data.data,
        installList: convertInstallListGubun(res.data.data.installList),
        scheduleRFList: res.data.data.scheduleRFList.map((el: { rfIndex: number; rfUrl: string; smIdx: number }) => ({
          fileId: el.rfIndex,
          file: '',
          fileName: el.rfUrl.slice(el.rfUrl.lastIndexOf('/') + 1),
          fileFormat: el.rfUrl.slice(el.rfUrl.lastIndexOf('.') + 1),
          fUrl: el.rfUrl,
        })),
      });
      if (type === 'save') {
        toast.success(t('저장되었습니다.'));
        await logPost({
          hCd: state.cCd,
          sCd: '00000',
          userId: state.userId,
          menu: '서비스 보고서 모달',
          action: '서비스 보고서 저장',
          etc: `일정명 : ${state.smName}`,
        });
      } else {
        await logPost({
          hCd: state.cCd,
          sCd: '00000',
          userId: state.userId,
          menu: '서비스 보고서 모달',
          action: '서비스 보고서 수정',
          etc: `일정명 : ${state.smName}`,
        });
      }
    } else {
      setIsSaveLoading(false);
    }
  };

  // 수정 확인 버튼 클릭
  const onClickConfirmEdit = () => {
    setOpenConfirmModal({ status: true, type: 'confirm', api: () => onClickSave('modify') });
  };

  const onClickSign = async () => {
    const completeDateDom = document.querySelector('.inputMask') as HTMLInputElement;

    if (!form.completeDate) {
      toast.error(t('완료일을 입력해주세요.'));
      completeDateDom && completeDateDom.focus();
    } else if (form.isInstall === 'N' && form.isMaintenance === 'N') {
      toast.error(t('설치 또는 유지보수 중 하나 이상을 선택해주세요.'));
    } else if (!form.signManagerName || !form.signManagerPhone) {
      setIsSaveClicked(true);
      toast.error(t('현장 서명자 정보를 입력해주세요.'));
    } else if ((form.scheduleRFList.filter((el: IFile) => el.fUrl === '').map((el: IFile) => el.file) || []).length > 0) {
      toast.info(t('첨부된 파일이 있습니다. 저장을 먼저 진행해주세요.'));
    } else if (deleteFileIndex.length > 0) {
      toast.info(t('삭제된 파일이 있습니다. 저장을 먼저 진행해주세요.'));
    } else {
      const {
        hName,
        sName,
        wDate,
        eDate,
        writer,
        editor,
        signImg,
        isSent,
        companyManagerName,
        companyManagerPhone,
        mainManagerName,
        mainManagerPhone,
        subManagerName,
        subManagerPhone,
        scheduleRFList,
        mainManagerSignUrl,
        subManagerSignUrl,
        installDate,
        // siteManagerName,
        // siteManagerPartname,
        // siteManagerPhone,
        ...rest
      } = form;
      const req = {
        ...rest,
        mainManagerId: mainManagerCd.mainManagerCd,
        subManagerId: subManagerCd.subManagerCd,
        installList: JSON.stringify(form.installList),
        installDate: JSON.stringify(form.installDate.map((el: string) => (el !== '' ? el : ''))),
      };
      const res = await apiPost({ path: '/schedule/report/view', req, contentType: 'application/json' });
      if (res.status === 200) {
        await logPost({
          hCd: state.cCd,
          sCd: '00000',
          userId: state.userId,
          menu: '서비스 보고서 모달',
          action: '서비스 보고서 서명 페이지 이동',
          etc: `일정명 : ${state.smName}`,
        });
        setIsSignView(true);
        // window.location.href = `/service_report/${form.smIdx}`;
      } else if (res.status === 400) {
        toast.warn(t('이미 전송된 보고서입니다.'));
      }
    }
  };

  // 설치/해체 테이블 행 추가/수정
  const handleInstallChange = (idx: number, key: string, value: any) => {
    setForm((prev: any) => {
      const list = [...prev.installList];
      list[idx][key] = value;
      return { ...prev, installList: list };
    });
  };
  const addInstallRow = () => {
    setForm((prev: any) => ({ ...prev, installList: [...prev.installList, { name: '', unit: '', qty: '', date: '', location: '', bigo: '', gubun: { name: '', gubun: '' } }] }));
  };
  const removeInstallRow = (idx: number) => {
    setForm((prev: any) => {
      const updatedList = [...prev.installList];
      updatedList.splice(idx, 1);
      return { ...prev, installList: updatedList.length ? updatedList : [{ name: '', unit: '', qty: '', date: '', location: '', bigo: '', gubun: { name: '', gubun: '' } }] };
    });
  };

  // 양식지 다운로드
  const onClickImageDownload = async () => {
    const dpi = window.devicePixelRatio;

    // Flutter WebView인 경우 고해상도 캡처를 위한 설정
    const isFlutterWebView = !!window.FlutterWebView;

    // Flutter WebView인 경우 고정 크기로 캡처 (1600*2180)
    const widthFix = isFlutterWebView ? 1600 : 800;
    const heightFix = isFlutterWebView ? 2180 : undefined;
    const scale = isFlutterWebView ? 4 : 2; // Flutter WebView는 scale 4로 초고해상도 캡처

    const style = document.createElement('style');
    document.head.appendChild(style);
    style.sheet?.insertRule('body > div:last-child img { display: inline-block; }');

    const cloneEl = screenElement.cloneNode(true) as HTMLElement;
    previewElement.innerHTML = '';
    previewElement.appendChild(cloneEl);

    const canvas = await html2canvas(previewElement, {
      scale,
      width: widthFix,
      height: heightFix,
      windowWidth: widthFix * dpi,
      windowHeight: heightFix ? heightFix * dpi : undefined,
      useCORS: true,
      allowTaint: true,
    });

    // Flutter 웹앱인 경우에만 여백 제거
    const finalCanvas = isFlutterWebView ? trimCanvas(canvas) : canvas;
    const base64 = finalCanvas.toDataURL('image/png', 1.0);

    if (base64) {
      await downloadFile(base64, `${form.hName}_${form.sName}_service_report.jpg`);
    }
  };

  const printComponentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
    documentTitle: '',
    pageStyle: `
      table {
        border-collapse: collapse !important;
        margin: 10px 24px !important;
        width: calc(100% - 48px) !important;
      }
      .file-download-wrap {
        display: none;
      }
    `, // 인쇄 시 첨부파일 영역 숨기기
  });
  const randomString = Math.random().toString(36).substring(7);

  if (isLoading) {
    return (
      <LoadingModalRoot>
        <PulseLoader color='rgb(0, 122, 255)' size='1rem' />
      </LoadingModalRoot>
    );
  }

  return (
    <ModalBackground>
      <Modal className='full-screen-modal'>
        <ReportRoot className={isPcViewport ? 'pc-viewport' : ''}>
          <div id='preview' />
          <div className='inputForm-head'>
            <span>{t(`서비스 보고서`)}</span>
            <div className='closeBtn' onClick={onBackPrev} role='presentation'>
              <span className='material-symbols-rounded'>close</span>
            </div>
          </div>
          <div className='inputForm-body' ref={printComponentRef}>
            <ServiceReport
              form={form}
              setForm={setForm}
              handleInstallChange={handleInstallChange}
              addInstallRow={addInstallRow}
              removeInstallRow={removeInstallRow}
              isEdit={!form.signImg}
              mainManagerCd={mainManagerCd}
              setMainManagerCd={setMainManagerCd}
              subManagerCd={subManagerCd}
              setSubManagerCd={setSubManagerCd}
              mainManagerOptions={mainManagerOptions}
              setMainManagerOptions={setMainManagerOptions}
              subManagerOptions={subManagerOptions}
              setSubManagerOptions={setSubManagerOptions}
              setDeleteFileIndex={setDeleteFileIndex}
              deleteFileIndex={deleteFileIndex}
              isSignView={isSignView}
              signSrc={dataUrl}
              signManagerNameRef={signManagerNameRef}
              signManagerPhoneRef={signManagerPhoneRef}
              isSaveClicked={isSaveClicked}
            />
            {isSignView && (
              <SignatureContainer className='signature-container'>
                <SignatureCanvasWrapper className='signature-canvas-wrapper' ref={signatureCanvasWrapperRef}>
                  {!isSigned && <SignatureHint>{t('서명')}</SignatureHint>}
                  <StyledSignatureCanvas
                    penColor='black'
                    velocityFilterWeight={1}
                    minWidth={2}
                    maxWidth={4}
                    ref={canvasRef}
                    backgroundColor='transparent'
                    clearOnResize={false}
                    onBegin={() => {
                      setSigned(true);
                    }}
                    onEnd={() => {
                      const img = canvasRef.current?.toDataURL('image/jpg') || '';
                      setDataUrl(img);
                    }}
                  />
                </SignatureCanvasWrapper>
              </SignatureContainer>
            )}
          </div>
          <div className='footer'>
            {isSignView ? (
              <>
                <BtnGhost className='clear' onClick={clearSignature}>
                  <span className='material-symbols-rounded'>ink_eraser</span>
                  {t('지우기')}
                </BtnGhost>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <BtnRed onClick={onClickSignCancel}>{t('취소')}</BtnRed>
                  <BtnBlue onClick={onClickSignSave}>{t('확인')}</BtnBlue>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <BtnGreen onClick={onClickSend} disabled={!!form.signImg}>
                    {t('전송')}
                  </BtnGreen>
                  <BtnViolet onClick={onClickSign} disabled={!!form.signImg}>
                    {t('서명하기')}
                  </BtnViolet>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {!!form.signImg && (
                    <BtnGhost onClick={onClickImageDownload}>
                      <span className='material-symbols-rounded'>download</span>
                      {!isPcViewport && t('다운로드')}
                    </BtnGhost>
                  )}
                  {!isMobile() && !!form.signImg && (
                    <BtnGhost onClick={handlePrint}>
                      <span className='material-symbols-rounded'>print</span>
                      {t('인쇄')}
                    </BtnGhost>
                  )}
                  {form.signImg ? (
                    <LoadButton text={t('수정')} color='blue' isLoading={isSaveLoading} onClick={onClickConfirmEdit} />
                  ) : (
                    <LoadButton text={t('저장')} color='blue' isLoading={isSaveLoading} onClick={() => onClickSave('save')} />
                  )}
                </div>
              </>
            )}
          </div>
        </ReportRoot>

        <Portal openModal={openConfirmModal.status}>
          {openConfirmModal.status && openConfirmModal.type === 'confirm' && (
            <ConfirmModal
              openModal={openConfirmModal}
              setOpenModal={setOpenConfirmModal}
              text={t('수정하시면 서명을 다시 요청해야 합니다.\n정말 수정하시겠습니까?')}
              confirmMessage={t('수정')}
              color='blue'
            />
          )}

          {openConfirmModal.status && openConfirmModal.type === 'sent_confirm' && (
            <ConfirmModal openModal={openConfirmModal} setOpenModal={setOpenConfirmModal} text={t('이미 전송된 보고서 입니다.\n재전송 하시겠습니까?')} confirmMessage={t('전송')} color='blue' />
          )}
        </Portal>
      </Modal>
    </ModalBackground>
  );
};

export default ServiceReportModal;
